/**
 * パスワード変更ページ（PRODUCT_DEV-293）
 *
 * 認証済みユーザーのパスワード変更フォーム。
 * 各プロダクト（Unizo等）から returnUrl パラメータ付きで遷移してくる。
 * 変更成功後は returnUrl にリダイレクトする。
 */
import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { AuthPageLayout } from "@/pages/LoginPage/components/AuthPageLayout";
import { LoadingButton } from "@/pages/LoginPage/components/LoadingButton";
import { PasswordRequirementsHint } from "@/pages/LoginPage/components/PasswordRequirementsHint";
import { PasswordField } from "@/pages/LoginPage/components/PasswordField";
import { PortalAuthClientWithSelfUI } from "@/lib/portalAuthClient";
import { validatePasswordRequirements } from "@/utils/passwordValidation";

export const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl") || "";

  const client = useMemo(() => new PortalAuthClientWithSelfUI(), []);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const passwordError = validatePasswordRequirements(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("新しいパスワード（確認用）が一致しません");
      return;
    }
    if (oldPassword === newPassword) {
      setError("現在のパスワードと異なるパスワードを設定してください");
      return;
    }

    setLoading(true);
    try {
      await client.changePassword(oldPassword, newPassword);
      setSuccess(true);
      // 成功後、少し待って遷移元に戻す
      setTimeout(() => {
        if (returnUrl) {
          window.location.href = returnUrl;
        } else {
          navigate("/");
        }
      }, 1500);
    } catch (err) {
      setError(mapCognitoError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (returnUrl) {
      window.location.href = returnUrl;
    } else {
      navigate("/");
    }
  };

  return (
    <AuthPageLayout>
      <h2 className="text-2xl font-bold text-center text-neutral-900 mb-6">
        パスワード変更
      </h2>

      <form onSubmit={handleSubmit} className="space-y-5">
        <PasswordField
          id="current-password"
          name="current-password"
          autoComplete="current-password"
          label="現在のパスワード"
          value={oldPassword}
          onChange={setOldPassword}
          show={showOld}
          onToggle={() => setShowOld((v) => !v)}
          disabled={loading}
          autoFocus
          variant="change"
        />
        <PasswordField
          id="new-password"
          name="new-password"
          autoComplete="new-password"
          label="新しいパスワード"
          value={newPassword}
          onChange={setNewPassword}
          show={showNew}
          onToggle={() => setShowNew((v) => !v)}
          disabled={loading}
          variant="change"
        />
        <PasswordField
          id="confirm-password"
          name="confirm-password"
          autoComplete="new-password"
          label="新しいパスワード（確認用）"
          value={confirmPassword}
          onChange={setConfirmPassword}
          show={showConfirm}
          onToggle={() => setShowConfirm((v) => !v)}
          disabled={loading}
          variant="change"
        />

        <PasswordRequirementsHint />

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
            {error}
          </div>
        )}
        {success && (
          <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
            パスワードを更新しました。元の画面に戻ります...
          </div>
        )}

        <LoadingButton
          loading={loading}
          loadingMessage="更新中..."
          label="パスワードを更新する"
        />

        <button
          type="button"
          onClick={handleBack}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
        >
          <ArrowLeft size={16} />
          基本設定に戻る
        </button>
      </form>
    </AuthPageLayout>
  );
};

/**
 * Cognitoエラーを日本語メッセージにマッピング
 */
function mapCognitoError(err: unknown): string {
  if (err instanceof Error) {
    const name = err.name;
    if (name === "NotAuthorizedException") {
      return "現在のパスワードが正しくありません";
    }
    if (name === "InvalidPasswordException") {
      return "パスワードの要件を満たしていません。上記のパスワード要件をご確認ください。";
    }
    if (name === "LimitExceededException") {
      return "試行回数の上限です。しばらくお待ちください";
    }
    return err.message || "パスワードの変更に失敗しました";
  }
  return "パスワードの変更に失敗しました";
}

export default ChangePasswordPage;
